import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useFormContext } from '../../context/FormContext';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

const { FiDownload } = FiIcons;

export const SummaryPanel = () => {
  const { data } = useFormContext();

  const stats = useMemo(() => {
    if (data.method === 'upload') {
      if (data.upload.workbook) {
        return {
          version: '1.0',
          transactions: '1,420',
          spend: '£4.2M', // Updated default for workbook upload preview
          assets: '34',
          lifecycle: 'Yes',
          mix: 'Valid',
          dist: 'Valid',
          sec3: 'Complete'
        };
      }
      return { version: '-', transactions: '0', spend: '0', assets: '0', lifecycle: '0', mix: '0', dist: '0', sec3: '0' };
    } else {
      const txCount = data.form.transactions.filter(t => t.amount).length;
      const spendTotal = data.form.annualSpend.reduce((sum, s) => sum + (Number(s.spend) || 0), 0);
      const assetCount = data.form.assets.filter(a => a.name).length;
      
      // Determine currency symbol based on first transaction
      const currency = data.form.transactions[0]?.currency === 'EUR' ? '€' : '£';

      return {
        version: 'Draft',
        transactions: txCount || '0',
        spend: spendTotal ? `${currency}${spendTotal.toLocaleString()}` : '0',
        assets: assetCount || '0',
        lifecycle: data.form.lifecycle.filter(l => l.name).length || '0',
        mix: ['new', 'repeat', 'subscription', 'oneOff'].reduce((sum, key) => sum + (Number(data.form.purchaseMix[key]) || 0), 0) === 100 ? '100%' : '0',
        dist: data.form.distribution.reduce((sum, d) => sum + (Number(d.percentage) || 0), 0) === 100 ? '100%' : '0',
        sec3: 'Active'
      };
    }
  }, [data]);

  const payload = useMemo(() => {
    if (!data.contact.firstName && !data.upload.workbook) return null;

    if (data.method === 'upload' && data.upload.workbook) {
      return {
        type: 'rfi_workbook_submission',
        timestamp: new Date().toISOString(),
        contact: data.contact,
        file: { name: data.upload.workbook.name, size: data.upload.workbook.size },
        parsedMetrics: stats
      };
    } else if (data.method === 'form') {
      return {
        type: 'rfi_web_form_submission',
        timestamp: new Date().toISOString(),
        contact: data.contact,
        data: data.form
      };
    }
    return null;
  }, [data, stats]);

  const downloadJson = () => {
    if (!payload) return;
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rfi_payload.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const Tile = ({ label, value }) => (
    <div className="p-3 bg-brand-light/30 border border-brand-border rounded-lg">
      <div className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">{label}</div>
      <div className="text-lg font-serif text-brand-text truncate">{value}</div>
    </div>
  );

  return (
    <div className="sticky top-8">
      <Card>
        <div className="mb-6">
          <h3 className="text-[10px] font-bold text-brand-muted uppercase tracking-widest mb-2">Summary</h3>
          <h2 className="text-xl font-serif text-brand-text">Structured payload snapshot.</h2>
          <p className="text-sm text-brand-muted mt-1">Latest parsed or submitted result.</p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-8">
          <Tile label="Version" value={stats.version} />
          <Tile label="Transactions" value={stats.transactions} />
          <Tile label="Annual spend" value={stats.spend} />
          <Tile label="Assets" value={stats.assets} />
          <Tile label="Lifecycle" value={stats.lifecycle} />
          <Tile label="Purchase mix" value={stats.mix} />
          <Tile label="Distribution" value={stats.dist} />
          <Tile label="Section 3" value={stats.sec3} />
        </div>

        <div className="mb-6">
          <h4 className="text-[10px] font-bold text-brand-muted uppercase tracking-widest mb-2 border-b border-brand-border pb-2">Latest Payload Preview</h4>
          <div className="bg-brand-charcoal-900 bg-[#222421] rounded-lg p-4 overflow-auto max-h-[300px]">
            {payload ? (
              <pre className="text-[10px] text-brand-light font-mono leading-relaxed">
                {JSON.stringify(payload, null, 2)}
              </pre>
            ) : (
              <p className="text-xs text-brand-muted/50 italic">Nothing submitted yet.</p>
            )}
          </div>
        </div>

        <Button 
          variant="outline" 
          className="w-full" 
          disabled={!payload}
          onClick={downloadJson}
        >
          <SafeIcon icon={FiDownload} className="mr-2" /> Download JSON
        </Button>
      </Card>
    </div>
  );
};